import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { RiskBadge } from "@/components/RiskBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Ship, 
  Building2, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  ArrowRight,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-[#0b3e65] to-[#1e5a8a] rounded-sm p-6 text-white">
          <h1 className="text-2xl font-serif mb-2">Welcome to Green Bunkers Compliance</h1>
          <p className="text-slate-200">
            Screen vessels and companies against OFAC, UN, and EU sanctions lists
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/vessels" data-testid="quick-vessel-search">
            <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-[#0b3e65]">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="p-3 bg-[#0b3e65]/10 rounded-sm">
                  <Ship className="w-8 h-8 text-[#0b3e65]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#0b3e65]">Vessel Search</h3>
                  <p className="text-sm text-slate-600">Search by IMO number or vessel name</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/companies" data-testid="quick-company-search">
            <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-[#f37022]">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="p-3 bg-[#f37022]/10 rounded-sm">
                  <Building2 className="w-8 h-8 text-[#f37022]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#0b3e65]">Company Search</h3>
                  <p className="text-sm text-slate-600">Screen companies against sanctions</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Total Reports</p>
                  <p className="text-3xl font-semibold text-[#0b3e65] mt-1">
                    {loading ? "..." : stats?.total_reports || 0}
                  </p>
                </div>
                <FileText className="w-10 h-10 text-slate-300" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Low Risk</p>
                  <p className="text-3xl font-semibold text-emerald-600 mt-1">
                    {loading ? "..." : stats?.risk_counts?.green || 0}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Medium Risk</p>
                  <p className="text-3xl font-semibold text-amber-600 mt-1">
                    {loading ? "..." : stats?.risk_counts?.yellow || 0}
                  </p>
                </div>
                <AlertTriangle className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">High Risk</p>
                  <p className="text-3xl font-semibold text-red-600 mt-1">
                    {loading ? "..." : stats?.risk_counts?.red || 0}
                  </p>
                </div>
                <XCircle className="w-10 h-10 text-red-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Reports */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-[#0b3e65]">Recent Reports</CardTitle>
              <Link to="/reports">
                <Button variant="ghost" size="sm" className="text-[#f37022]" data-testid="view-all-reports">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : stats?.recent_reports?.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {stats.recent_reports.map((report) => (
                  <Link
                    key={report.id}
                    to={`/reports/${report.id}`}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`recent-report-${report.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Ship className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-[#0b3e65]">
                          {report.vessel_data?.vessel_name || "Unknown Vessel"}
                        </p>
                        <p className="text-sm text-slate-500">
                          IMO: {report.vessel_data?.imo_number || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <RiskBadge risk={report.overall_risk} size="sm" />
                      <div className="text-right text-sm text-slate-500">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {formatDate(report.created_at)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No reports yet</p>
                <p className="text-sm text-slate-400">Start by searching for a vessel or company</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Sources Info */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-serif text-[#0b3e65]">Sanctions Data Sources</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-sm">US</span>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">OFAC SDN List</h4>
                  <p className="text-sm text-slate-500">U.S. Treasury Department</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-700 font-bold text-sm">UN</span>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">UN Security Council</h4>
                  <p className="text-sm text-slate-500">United Nations</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-700 font-bold text-sm">EU</span>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">EU Consolidated</h4>
                  <p className="text-sm text-slate-500">European Union</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-rose-700 font-bold text-sm">UK</span>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">UK Sanctions List</h4>
                  <p className="text-sm text-slate-500">OFSI / HM Treasury</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DashboardPage;
